import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { COLLECTIONS } from '@/services/firestore/collections';
import { withIdConverter } from '@/services/firestore/converters';
import type { GiftCard, Sale } from '@/services/firestore/types';

const saleConverter = withIdConverter<Sale>();
const giftCardConverter = withIdConverter<GiftCard>();

export const SALES_QUERY_KEY = ['sales'];
export const GIFT_CARDS_QUERY_KEY = ['giftCards'];

export function saleQueryKey(saleId: string) {
  return [...SALES_QUERY_KEY, saleId] as const;
}

export async function getSaleById(saleId: string) {
  try {
    const snapshot = await getDoc(
      doc(db, COLLECTIONS.SALES, saleId).withConverter(saleConverter),
    );

    return snapshot.exists() ? snapshot.data() : null;
  } catch (error) {
    console.error('Error fetching sale:', error);
    throw error;
  }
}

export async function listRecentSales(resultLimit = 20) {
  try {
    const salesQuery = query(
      collection(db, COLLECTIONS.SALES).withConverter(saleConverter),
      orderBy('createdAt', 'desc'),
      limit(resultLimit),
    );
    const snapshot = await getDocs(salesQuery);

    return snapshot.docs.map((sale) => sale.data());
  } catch (error) {
    console.error('Error listing recent sales:', error);
    throw error;
  }
}

/**
 * Sales whose `createdAt` falls within a single day range. `createdAt` is
 * written as a Firestore server timestamp, so the ISO bounds are converted to
 * `Timestamp` for the range comparison.
 */
export async function listSalesForRange(
  startAt: string,
  endAt: string,
  resultLimit = 500,
) {
  try {
    const salesQuery = query(
      collection(db, COLLECTIONS.SALES).withConverter(saleConverter),
      where('createdAt', '>=', Timestamp.fromDate(new Date(startAt))),
      where('createdAt', '<', Timestamp.fromDate(new Date(endAt))),
      orderBy('createdAt', 'desc'),
      limit(resultLimit),
    );
    const snapshot = await getDocs(salesQuery);

    return snapshot.docs.map((sale) => sale.data());
  } catch (error) {
    console.error('Error listing sales for range:', error);
    throw error;
  }
}

export async function listGiftCards(resultLimit = 20) {
  try {
    const giftCardsQuery = query(
      collection(db, COLLECTIONS.GIFT_CARDS).withConverter(giftCardConverter),
      orderBy('createdAt', 'desc'),
      limit(resultLimit),
    );
    const snapshot = await getDocs(giftCardsQuery);

    return snapshot.docs.map((giftCard) => giftCard.data());
  } catch (error) {
    console.error('Error listing gift cards:', error);
    throw error;
  }
}

/** Exact-match lookup for a staff-entered gift card code (tries each candidate format). */
export async function findGiftCardByCode(candidates: string[]) {
  try {
    for (const candidate of candidates) {
      const giftCardsQuery = query(
        collection(db, COLLECTIONS.GIFT_CARDS).withConverter(giftCardConverter),
        where('code', '==', candidate),
        limit(1),
      );
      const snapshot = await getDocs(giftCardsQuery);

      if (!snapshot.empty) {
        return snapshot.docs[0].data();
      }
    }

    return null;
  } catch (error) {
    console.error('Error finding gift card by code:', error);
    throw error;
  }
}
